import type { RoomsParticipantDistribution } from '@/domain/shared/types'
import type { AccommodationTip } from '@/scopes/search/search/infrastructure/api/types'
import { mqMin, space, tok, tokPx } from '@sunwebgroup/ui-components'
import { Icon, Typography } from '@sunwebgroup/ui-components/uds'
import { css, styled } from 'styled-components'
import { LazyImage } from '@/scopes/common/components/Image'
import { PriceInfo } from '@/scopes/common/components/PriceInfo'
import { LocationTag } from '@/scopes/search/search/pages/SearchPage/components/Results/ProductCard/LocationTag'
import { OverlayLink } from '@/scopes/search/search/pages/SearchPage/components/Results/ProductCard/OverlayLink'
import { ScoreRatings } from '@/scopes/search/search/pages/SearchPage/components/Results/ProductCard/ScoresRatings'

export interface AccommodationCardProps extends AccommodationTip {
  $orientation?: 'top' | 'bottom'
  slideIndex?: number
  participants?: RoomsParticipantDistribution
}

const Heading = styled(Typography).attrs({ forwardedAs: 'span', variant: 'display-g-regular' })`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
`

const Card = styled.div`
  position: relative;
  width: 100%;
  width: 100%;
  height: 480px;

  @media ${mqMin('primitives_breakpoint_m_min')} {
    height: 320px;
  }
`

const InfoWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`

const HeadingWrapper = styled.div`
  margin-bottom: ${space('primitives_space_12')};
`

const Content = styled.div.attrs<{ $orientation?: 'top' | 'bottom' }>(props => ({
  $orientation: props.$orientation || 'bottom',
}))`
  position: absolute;
  left: ${space('primitives_space_24')};
  right: ${space('primitives_space_24')};
  background: ${tok('semantics_content_white_color')};
  border-radius: ${tokPx('semantics_cards_corner_radius')};
  padding: ${space('primitives_space_24')};

  ${({ $orientation }) =>
    $orientation === 'top'
      ? css`
          top: ${space('primitives_space_24')};
        `
      : css`
          bottom: ${space('primitives_space_40')};
        `};

  @media ${mqMin('primitives_breakpoint_m_min')} {
    width: 352px;
    top: ${space('primitives_space_48')};
    bottom: ${space('primitives_space_48')};
    left: ${space('primitives_space_48')};
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
`

const StyledOverlayLink = styled(OverlayLink)`
  z-index: 1; // So that the link overlay is on top of the slider
`

const StyledPriceInfo = styled(PriceInfo)`
  button {
    z-index: 2; // This is a fix for the button not being clickable in the overlay link
  }
`

const AirportNameContainer = styled.div`
  color: ${tok('semantics_content_secondary_color')};
  display: flex;
  bottom: -12px;
  top: 3px;
  position: relative;
`

const AirportName = styled.div`
  padding-right: ${space('primitives_space_12')};
  display: flex;

  span {
    margin-right: ${space('primitives_space_8')};
  }
`

export default function AccommodationCard({
  $orientation,
  arrivalDate,
  departureDate,
  id,
  images,
  locations,
  mealPlan,
  name,
  occupancy,
  price,

  rating,
  slideIndex,
  url,
  userRating,
  userRatingText,
  outboundDepartureAirportName,
  participants,
  acmInformation,
}: AccommodationCardProps) {
  return (
    <Card>
      <StyledOverlayLink href={url} aria-label={name} target="_blank" />
      <LazyImage
        src={images[0] || ''}
        alt={name}
        srcSet={[560, 940, 1200]}
        sizes="(min-width: 1280px) 936px, 100vw"
        loading={slideIndex && slideIndex > 1 ? 'lazy' : 'eager'}
      />
      <Content $orientation={$orientation}>
        <HeadingWrapper>
          <ScoreRatings rating={rating} userRating={userRating} aria-label={userRatingText} />
          <Heading>{name}</Heading>
          <LocationTag locations={locations} />
        </HeadingWrapper>
        <InfoWrapper>
          <div>
            <Typography forwardedAs="div" variant="body-s-bold-snug">
              {departureDate}
              {' '}
              -
              {' '}
              {arrivalDate}
            </Typography>
            <Typography forwardedAs="div" variant="body-s-regular-snug">
              {mealPlan}
              ,
              {occupancy}
            </Typography>
            {outboundDepartureAirportName && (
              <AirportNameContainer>
                <AirportName>
                  <Icon glyph="AirplaneTakingOff" size={16} />
                  <Typography forwardedAs="div" variant="body-xs-regular-tight">
                    {outboundDepartureAirportName}
                  </Typography>
                </AirportName>
              </AirportNameContainer>
            )}
          </div>
          <div>
            {price && (
              <StyledPriceInfo
                accommodationId={id}
                averagePrice={price}
                size="M"
                participants={participants}
                acmInformation={acmInformation}
              />
            )}
          </div>
        </InfoWrapper>
      </Content>
    </Card>
  )
}
